import { GridCellParams } from '@mui/x-data-grid-pro';
import Chip from '@mui/material/Chip';
import { v4 } from 'uuid';
import { GridCustomColDef } from '../../types';
import {
  advancedOperators,
  advancedDatePickerOperator,
  advancedNumericOperators,
  advancedEnumOperators,
  runPickerOperators
} from '../../Compounds/UpstackDataGrid/helpers';
import { chipStyles, formatPrice, getDate, getFormattedDate } from '../../../utils/helpers';
import { Correction } from '../../../store/Correction';
import { DetailCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers';
import { getCommissionsInfoByType } from '../../../store/GeneralStore';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { Text } from '../../Atoms/Text';
import { handleAssetClick } from '../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';

export const DefaultCorrectionGridColumnDefs = (): GridCustomColDef[] => [
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'details',
    headerName: '',
    sortable: false,
    width: 65,
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
        state={{
          id: v4(),
          data: params.row,
          ...getMetaData({ name: ComponentName.CORRECTION, id: params.row.id, data: params.row }),
          replace: false
        }}
      />
    ),
    hideable: false,
    pinned: 'left',
    disableReorder: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'paidInRunDate',
    headerName: 'Paid In',
    sortable: true,
    filterable: true,
    minWidth: 80,
    flex: 0.5,
    description: 'Drag to move column',
    renderCell: (params: GridCellParams) => {
      const date = params.value as string;
      if (!date) return '';
      return getFormattedDate(date, 'MMM YY');
    },
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'correctsRunDate',
    headerName: 'Corrects',
    sortable: true,
    filterable: true,
    minWidth: 80,
    flex: 0.5,
    description: 'Drag to move column',
    renderCell: (params: GridCellParams) => {
      const date = params.value as string;
      if (!date) return '';
      return getFormattedDate(date, 'MMM YY');
    },
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'revenueRecurringCorrection',
    headerName: 'Recurring',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams<Correction>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    }
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'revenueOneTimeCorrection',
    headerName: 'One-Time',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams<Correction>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    }
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'combinedAssetNumber',
    headerName: 'Asset',
    sortable: true,
    filterable: true,
    minWidth: 160,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedOperators(),
    renderCell: (params: GridCellParams<Correction>) => {
      const type = params.row.assetSfId ? ComponentName.ASSET : ComponentName.PROVISIONAL_ASSET;
      return (
        <button
          className="flex items-center"
          type="button"
          onClick={() => handleAssetClick(params.row.combinedAssetId, type)}>
          <Text
            size="sm14"
            color="indigo">
            {params?.row.combinedAssetNumber as string}
          </Text>
        </button>
      );
    }
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'note',
    headerName: 'Note',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 2,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'createdBy.name',
    headerName: 'Added By',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    renderCell: (params: GridCellParams<Correction>) => {
      const value = params.row.createdBy;
      if (!value) return '';
      return `${value.firstName} ${value.lastName}`.trim() || value.email;
    },
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'updatedBy.name',
    headerName: 'Modified By',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    renderCell: (params: GridCellParams<Correction>) => {
      const value = params.row.updatedBy;
      if (!value) return '';
      return `${value.firstName} ${value.lastName}`.trim() || value.email;
    },
    filterOperators: advancedOperators(),
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'createdAt',
    headerName: 'Added At',
    sortable: true,
    filterable: true,
    minWidth: 150,
    flex: 1,
    description: 'Drag to move column',
    valueFormatter: (value) => getFormattedDate(value),
    valueGetter: (value) => (value ? new Date(value) : ''),
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('account', 'suppliers'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'supplierName',
    headerName: 'Supplier',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators(),
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'customerName',
    headerName: 'Customer',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedOperators(),
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('account', 'salesAgents'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'salesAgentName',
    headerName: 'Sales Agent',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators(),
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'tsdName',
    headerName: 'TSD',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedOperators(),
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('asset', 'commissionsStatus'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    align: 'center',
    field: 'commissionsStatus',
    renderCell: (params: GridCellParams) => {
      const status = params.value as string;
      if (!status) return '';
      return (
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    },
    headerName: 'Commissions Status',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators(),
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'actualFirstCommissionRun',
    headerName: 'First Commission Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: runPickerOperators(),
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'actualDecommissionedDate',
    headerName: 'Decommission Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: runPickerOperators(),
    hideByDefault: true
  }
];
